import { ChangeDetectorRef, Component, Input, OnChanges, SimpleChanges, ViewChild } from '@angular/core';
import { ChannelSearchDialogComponent } from '@components/channel-search-dialog/channel-search-dialog.component';
import { ChannelService } from '@service/channel/channel.service';
import { ChannelGroupService } from '@service/channel/channel-group.service';
import { ChannelCategoryService } from '@service/channel/channel-category.service';
import { Channel } from 'icreate/common/models/channel';
import { ChannelGroup } from 'icreate/common/models/channel-group';
import { ChannelCategory } from 'icreate/common/models/channel-category';
import { lastValueFrom, take } from 'rxjs';
import { ChannelType, getAnalogTypesSet, getDigitalTypesSet, isDigital } from 'icreate/common/models/channel-type';
import { ElementSettings, ValveSetting } from 'icreate/common/models/editor-viewer-element';
import * as _ from 'lodash';
import { ElementSettingsService } from '@service/canvas/element-settings.service';
import { EditorService } from '@service/editor.service';
import { ElementAttribute } from '@models/editor';
import { Vessel } from 'icreate/common/models/vessel';
import { ElementIntegrationViewData } from 'icreate/common/models/element-integration';
import { DefaultColorName, SystemColor } from 'icreate/common/models/system-color';
import { CanvasService } from '@service/canvas/canvas.service';
import { ElementOrientationService } from '@service/canvas/element-orientation.service';
import { CanvasInputMode } from '@service/canvas/canvas-input-mode.service';
import { AutosavePage } from '@service/auto-save-controller.service';
import { MatDialog } from '@angular/material/dialog';

class ChannelAndGroup extends Channel {
    group: ChannelGroup;
}

export enum ValveActionType {
    OPEN = 'open',
    CLOSE = 'close',
}

export enum ValveDisplayState {
    STATIC = 'static',
    CLOSED = 'closed',
}

@Component({
    selector: 'app-valve-object-setting',
    templateUrl: './valve-object-setting.component.html',
    styleUrls: ['./valve-object-setting.component.css'],
    standalone: false,
})
export class ValveObjectSettingComponent implements OnChanges {
    @Input() selectedElement;
    @Input() mimicId: string;
    @Input() vessel: Vessel;
    @Input() userCanEdit: boolean;
    @Input() elementSettings: ElementSettings;
    @Input() elementIntegration: ElementIntegrationViewData;
    @Input() systemColors: SystemColor[];

    @ViewChild('percentagePositionDropdown') percentagePositionDropdown;

    readonly ValveActionType = ValveActionType;

    isOpenToggleEnabled: boolean = false;
    isCloseToggleEnabled: boolean = false;

    allowedTypesSetForChannel: Set<ChannelType> = new Set();
    shouldDisableChannelTypes: boolean = false;

    selectedOpenChannel: ChannelAndGroup;
    selectedCloseChannel: ChannelAndGroup;

    selectedOpenFlashChannel: ChannelAndGroup;
    selectedCloseFlashChannel: ChannelAndGroup;

    openingCountItemList = [
        'mimic.settings.valve-object.appearance.opening-count.2way',
        'mimic.settings.valve-object.appearance.opening-count.3way',
    ];

    orientationItemList = [
        'mimic.settings.valve-object.appearance.orientation.horizontal',
        'mimic.settings.valve-object.appearance.orientation.vertical',
    ];
    selectedOpeningCount = this.openingCountItemList[0];
    fuUnresponsiveColor: string = '';
    selectedOrientation = this.orientationItemList[0];

    openChannelStateItemList: string[] = [];
    selectedOpenChannelState: string = '';
    openChannelStateColor: string = '';

    closeChannelStateItemList: string[] = [];
    selectedCloseChannelState: string = '';
    closeChannelStateColor: string = '';

    selectedOpenPercentage: number = 100;
    selectedOpenPercentagePosition: string = 'bottom';
    selectedOpenPercentagePositionKey: string = 'mimic.settings.valve-object.interaction.position.bottom';

    selectedClosePercentage: number = 100;
    selectedClosePercentagePosition: string = 'bottom';
    selectedClosePercentagePositionKey: string = 'mimic.settings.valve-object.interaction.position.bottom';

    percentageMinValue: number = 0;
    percentageMaxValue: number = 100;
    percentagePositionItemList: string[] = [
        'mimic.settings.valve-object.interaction.position.bottom',
        'mimic.settings.valve-object.interaction.position.top',
        'mimic.settings.valve-object.interaction.position.right',
        'mimic.settings.valve-object.interaction.position.left',
    ];
    private percentagePositionMap: { [key: string]: string } = {
        'mimic.settings.valve-object.interaction.position.bottom': 'bottom',
        'mimic.settings.valve-object.interaction.position.top': 'top',
        'mimic.settings.valve-object.interaction.position.right': 'right',
        'mimic.settings.valve-object.interaction.position.left': 'left',
    };

    private elementSettingsId: string;
    private initialValveSetting: ValveSetting;

    private static readonly DEFAULT_COLOR_FALLBACK: { [key: string]: string } = {
        [DefaultColorName.DIGITAL_ON]: 'rgba(32, 228, 32, 1)',
        [DefaultColorName.DIGITAL_OFF]: 'rgba(255, 255, 255, 1)',
        [DefaultColorName.FU_UNRESPONSIVE]: 'rgba(192, 192, 192, 1)',
    };

    private static readonly DEFAULT_VALVE_BODY_FILL = '#607d8b';

    private static readonly ANALOG_CLOSED_MAX_PERCENTAGE = 3;

    private static readonly VALVE_3WAY_ICON_BY_ORIENTATION: Record<'horizontal' | 'vertical', string> = {
        horizontal: 'valve-3-hori-open-3-way-open-horizontal-no-indicator',
        vertical: 'valve-3-vert-open-3-way-open-vertical-no-indicator',
    };

    constructor(
        private channelService: ChannelService,
        private channelGroupService: ChannelGroupService,
        private channelCategoryService: ChannelCategoryService,
        private editorService: EditorService,
        private elementSettingsService: ElementSettingsService,
        private canvasService: CanvasService,
        private elementOrientationService: ElementOrientationService,
        private cdr: ChangeDetectorRef,
        private dialog: MatDialog
    ) {}

    async ngOnChanges(changes: SimpleChanges) {
        if (!changes['selectedElement'] && !changes['elementSettings']) {
            return;
        }
        if (!this.isValveElement()) {
            return;
        }

        this.elementSettingsId = this.elementSettings?.id;
        this.resetValveState();
        await this.restoreValveSetting(this.elementSettings?.valveSetting);
        this.updateOrientationFromElement();
        this.updateOpeningCountFromElement();
        this.fuUnresponsiveColor = this.getSystemColorByName(DefaultColorName.FU_UNRESPONSIVE);
        this.initialValveSetting = _.cloneDeep(this.elementSettings?.valveSetting ?? null);
        this.cdr.detectChanges();
    }

    private getOppositeActionType(type: ValveActionType): ValveActionType {
        return type === ValveActionType.OPEN ? ValveActionType.CLOSE : ValveActionType.OPEN;
    }

    isValveElement(): boolean {
        return this.selectedElement?.getAttribute(ElementAttribute.iCreateType) === CanvasInputMode.DEFAULT_VALVE;
    }

    getSelectedElementId() {
        return this.selectedElement ? this.selectedElement.getAttribute(ElementAttribute.id) : '';
    }

    onOpeningCountChange(event: { name: string; value: string }) {
        this.selectedOpeningCount = event.value;
        if (this.selectedElement) {
            this.updateValveIcon();
        }
    }

    onOrientationChange(event: { name: string; value: string }) {
        this.selectedOrientation = event.value;
        if (this.selectedElement) {
            this.updateValveIcon();
        }
    }

    private getResolvedOrientation(): 'horizontal' | 'vertical' {
        return this.selectedOrientation?.includes('horizontal') ? 'horizontal' : 'vertical';
    }

    updateOrientationFromElement(): void {
        if (!this.selectedElement) return;

        const path = (this.selectedElement as SVGGElement).querySelector?.('path');
        if (!path) return;

        try {
            const pathBbox = (path as any).getBBox();
            const isHorizontal = pathBbox.width >= pathBbox.height;
            this.selectedOrientation = isHorizontal ? this.orientationItemList[0] : this.orientationItemList[1];
        } catch (err) {
            console.error('Failed to determine valve orientation:', err);
        }
    }

    updateOpeningCountFromElement(): void {
        if (!this.selectedElement) return;

        const way = (this.selectedElement as SVGGElement).getAttribute('data-icreate-valve-way');
        if (way === '3way') {
            this.selectedOpeningCount = this.openingCountItemList[1];
        } else if (way === '2way') {
            this.selectedOpeningCount = this.openingCountItemList[0];
        }
    }

    async openActionChannelSelector(type: ValveActionType) {
        const oppositeType = this.getOppositeActionType(type);
        const oppositeChannel = this.getSelectedChannel(oppositeType);

        if (oppositeChannel) {
            const oppositeChannelType = oppositeChannel.fcuData?.channelType;
            this.allowedTypesSetForChannel = new Set(
                isDigital(oppositeChannelType) ? getDigitalTypesSet() : getAnalogTypesSet()
            );
            this.shouldDisableChannelTypes = true;
        } else {
            this.allowedTypesSetForChannel = new Set();
            this.shouldDisableChannelTypes = false;
        }
        const allowedTypesSet = this.shouldDisableChannelTypes ? this.allowedTypesSetForChannel : undefined;

        const dialogRef = this.dialog.open(ChannelSearchDialogComponent, {
            data: {
                vesselId: this.vessel?.id,
                assignedChannel: null,
                allowedTypesSet: allowedTypesSet,
                setShouldDisableTypes: this.shouldDisableChannelTypes,
                multipleSelection: false,
                preSelectedChannelsCount: 0,
                stopAutoSave: true,
                autoSavePage: AutosavePage.MIMIC,
            },
        });
        const channelSelected: Channel = await lastValueFrom(dialogRef.afterClosed());

        if (channelSelected) {
            const channel = await this.enrichChannelWithGroup(channelSelected);
            if (channel) {
                this.setSelectedChannel(type, channel);
                this.setSelectedFlashChannel(type, undefined);
                this.setToggleEnabled(type, false);
                this.setupChannelStateView(type);
            }
        }

        this.allowedTypesSetForChannel = new Set();
        this.shouldDisableChannelTypes = false;
    }

    removeChannel(type: ValveActionType) {
        this.setSelectedChannel(type, undefined);
        this.setSelectedFlashChannel(type, undefined);
        this.setToggleEnabled(type, false);
        this.setChannelStateItems(type, []);
        this.setSelectedChannelState(type, '');
        this.setChannelStateColor(type, '');

        const oppositeType = this.getOppositeActionType(type);
        if (this.getSelectedChannel(oppositeType)) {
            this.setupChannelStateView(oppositeType);
        } else {
            this.removePercentageIndicator();
            this.resetValveIconToDefault();
        }
    }

    isChannelDigital(type: ValveActionType): boolean {
        const channelType = this.getSelectedChannel(type)?.fcuData?.channelType;
        return channelType != null && isDigital(channelType);
    }

    isChannelDigitalByChannel(channel: Channel): boolean {
        const channelType = channel?.fcuData?.channelType;
        return channelType != null && isDigital(channelType);
    }

    isChannelAnalog(type: ValveActionType): boolean {
        const channelType = this.getSelectedChannel(type)?.fcuData?.channelType;
        return channelType != null && !isDigital(channelType);
    }

    shouldShowPercentagePositionDropdown(type: ValveActionType = ValveActionType.OPEN): boolean {
        const percentage = this.getPercentage(type);
        return percentage > this.percentageMinValue && percentage < this.percentageMaxValue;
    }

    onFlashToggleChange(type: ValveActionType, checked: boolean) {
        this.setToggleEnabled(type, checked);
        if (!checked) {
            this.setSelectedFlashChannel(type, undefined);
        }
    }

    async openFlashChannelSelector(type: ValveActionType) {
        const dialogRef = this.dialog.open(ChannelSearchDialogComponent, {
            data: {
                vesselId: this.vessel?.id,
                assignedChannel: null,
                allowedTypesSet: this.allowedTypesSetForChannel,
                setShouldDisableTypes: this.shouldDisableChannelTypes,
                multipleSelection: false,
                preSelectedChannelsCount: 0,
                stopAutoSave: true,
                autoSavePage: AutosavePage.MIMIC,
            },
        });
        const channelSelected: Channel = await lastValueFrom(dialogRef.afterClosed());
        if (channelSelected) {
            const channel = await this.enrichChannelWithGroup(channelSelected);
            if (channel) {
                this.setSelectedFlashChannel(type, channel);
            }
        }
    }

    onChannelStateChange(type: ValveActionType, event: { name: string; value: string }) {
        this.setSelectedChannelState(type, event.value);
        this.updateChannelStateColor(type);
    }

    onPercentageInput(event: any) {
        const value = event.target?.value;
        if (value == null) return;
        const numValue = Number(value);
        if (isNaN(numValue)) return;
        this.applyPercentage(this.clampPercentage(numValue));
    }

    onPercentageChange(event: any) {
        const value = event.target?.value;
        if (value == null) return;
        const numValue = Number(value);
        const percentage = isNaN(numValue) ? this.percentageMaxValue : this.clampPercentage(numValue);
        this.applyPercentage(percentage);
    }

    onPercentagePositionChange(event: { name: string; value: string }) {
        const positionKey = event.value;
        const position = this.percentagePositionMap[positionKey] || positionKey;
        this.selectedOpenPercentagePositionKey = positionKey;
        this.selectedOpenPercentagePosition = position;
        this.selectedClosePercentagePositionKey = positionKey;
        this.selectedClosePercentagePosition = position;
        this.addPercentageIndicator();
    }

    getPercentagePositionKeyByValue(position: string): string {
        for (const [key, value] of Object.entries(this.percentagePositionMap)) {
            if (value === position) {
                return key;
            }
        }
        return position;
    }

    private clampPercentage(value: number): number {
        return Math.min(this.percentageMaxValue, Math.max(this.percentageMinValue, value));
    }

    private applyPercentage(percentage: number) {
        this.selectedOpenPercentage = percentage;
        this.selectedClosePercentage = percentage;

        const displayType = this.getValveDisplayType();
        if (this.isChannelAnalog(displayType)) {
            this.updateChannelAnalogColor(displayType);
        } else {
            this.addPercentageIndicator();
        }
    }

    private setupChannelStateView(type: ValveActionType) {
        const channel = this.getSelectedChannel(type);
        if (this.isChannelDigital(type)) {
            const onLabel = channel?.fcuData?.digitalAlarmSetting?.statusStringContactOn || 'ON';
            const offLabel = channel?.fcuData?.digitalAlarmSetting?.statusStringContactOff || 'OFF';
            this.setChannelStateItems(type, [onLabel, offLabel]);
            this.setSelectedChannelState(type, type === ValveActionType.OPEN ? onLabel : offLabel);
            this.updateChannelStateColor(type);
        } else if (this.isChannelAnalog(type)) {
            this.setChannelStateItems(type, []);
            this.setSelectedChannelState(type, '');
            this.setPercentage(type, 100);
            this.updateChannelAnalogColor(type);
        } else {
            this.setChannelStateItems(type, []);
            this.setSelectedChannelState(type, '');
            this.setChannelStateColor(type, '');
        }
        this.cdr.detectChanges();
    }

    private updateChannelStateColor(type: ValveActionType) {
        const channel = this.getSelectedChannel(type);
        let colorName: DefaultColorName;
        if (type === ValveActionType.OPEN) {
            const onLabel = channel?.fcuData?.digitalAlarmSetting?.statusStringContactOn || 'ON';
            const isOn = this.getSelectedChannelState(type) === onLabel;
            colorName = isOn ? DefaultColorName.DIGITAL_ON : DefaultColorName.DIGITAL_OFF;
        } else {
            const offLabel = channel?.fcuData?.digitalAlarmSetting?.statusStringContactOff || 'OFF';
            const isOff = this.getSelectedChannelState(type) === offLabel;
            colorName = isOff ? DefaultColorName.DIGITAL_OFF : DefaultColorName.DIGITAL_ON;
        }
        const color = this.getSystemColorByName(colorName);
        this.setChannelStateColor(type, color);
        this.applyColorToElement(type, color);
    }

    private updateChannelAnalogColor(type: ValveActionType) {
        const colorName = type === ValveActionType.OPEN ? DefaultColorName.DIGITAL_ON : DefaultColorName.DIGITAL_OFF;
        const color = this.getSystemColorByName(colorName);
        this.setChannelStateColor(type, color);
        const elementColor = this.isAnalogClosed() ? this.getSystemColorByName(DefaultColorName.DIGITAL_OFF) : color;
        this.applyColorToElement(type, elementColor);
    }

    private applyColorToElement(type: ValveActionType, color: string) {
        if (!this.selectedElement) return;

        const iCreateType = this.selectedElement.getAttribute(ElementAttribute.iCreateType);
        if (iCreateType !== CanvasInputMode.DEFAULT_VALVE) return;

        if (type !== this.getValveDisplayType()) return;

        const group = this.selectedElement as SVGGElement;
        const formattedColor = this.formatColorForCSS(color);
        const is3Way = this.selectedOpeningCount.includes('3way');

        if (is3Way) {
            this.applyValveIcon3Way(color);
            return;
        }

        this.elementOrientationService.applyValveOrientation(
            this.selectedElement,
            this.getResolvedOrientation(),
            this.getValveDisplayState()
        );

        const path = group.querySelector('path') as SVGPathElement | null;
        if (path && formattedColor) {
            path.setAttribute('fill', formattedColor);
            path.setAttribute('stroke', '#2c3a48');
            path.setAttribute('stroke-width', '1.5');
            path.style.fill = formattedColor;
        }

        if (this.isChannelAnalog(type)) {
            this.addPercentageIndicator();
        } else {
            this.refreshSelectionFrame(group);
        }
    }

    private removePercentageIndicator() {
        if (!this.selectedElement) return;

        const group = this.selectedElement as SVGGElement;
        group.querySelectorAll('text[data-percentage]').forEach(text => text.remove());
    }

    private addPercentageIndicator() {
        if (!this.selectedElement) return;

        const group = this.selectedElement as SVGGElement;

        const existingTexts = group.querySelectorAll('text[data-percentage]');
        existingTexts.forEach(text => text.remove());

        if (
            this.selectedOpenPercentage <= this.percentageMinValue ||
            this.selectedOpenPercentage >= this.percentageMaxValue
        ) {
            this.refreshSelectionFrame(group);
            return;
        }

        const circle = group.querySelector('circle') as SVGCircleElement | null;
        if (!circle) return;

        const cx = Number(circle.getAttribute('cx')) || 0;
        const cy = Number(circle.getAttribute('cy')) || 0;
        const r = Number(circle.getAttribute('r')) || 10;

        const isClosed = group.getAttribute('data-icreate-valve-state') === 'closed';

        let existingText = document.createElementNS('http://www.w3.org/2000/svg', 'text');
        existingText.setAttribute('data-percentage', 'true');
        existingText.setAttribute('font-family', 'NotoSans');
        existingText.setAttribute('font-size', '12');
        existingText.setAttribute('font-weight', 'bold');
        existingText.setAttribute('fill', isClosed ? '#ffffff' : 'rgb(32, 228, 32)');
        existingText.setAttribute('text-anchor', 'middle');
        group.appendChild(existingText);

        const offset = 20;
        const position = this.selectedOpenPercentagePosition;

        switch (position) {
            case 'bottom':
                existingText.setAttribute('x', cx.toString());
                existingText.setAttribute('y', (cy + r + offset).toString());
                existingText.setAttribute('text-anchor', 'middle');
                break;
            case 'top':
                existingText.setAttribute('x', cx.toString());
                existingText.setAttribute('y', (cy - r - offset + 12).toString());
                existingText.setAttribute('text-anchor', 'middle');
                break;
            case 'right':
                existingText.setAttribute('x', (cx + r + offset).toString());
                existingText.setAttribute('y', (cy + 4).toString());
                existingText.setAttribute('text-anchor', 'start');
                break;
            case 'left':
                existingText.setAttribute('x', (cx - r - offset).toString());
                existingText.setAttribute('y', (cy + 4).toString());
                existingText.setAttribute('text-anchor', 'end');
                break;
        }

        existingText.textContent = `${this.selectedOpenPercentage}%`;
        this.refreshSelectionFrame(group);
    }

    private refreshSelectionFrame(group: SVGGElement) {
        const canvas = this.canvasService.getCanvas();
        canvas.call('changed', [group]);
        const selector = canvas.selectorManager?.requestSelector(group);
        if (selector) {
            selector.resize();
        }
    }

    private getSystemColorByName(name: DefaultColorName): string {
        const fallback = ValveObjectSettingComponent.DEFAULT_COLOR_FALLBACK[name] ?? '';
        const match = this.systemColors?.find(c => c.name === name);
        return match?.color || fallback;
    }

    private formatColorForCSS(color: string): string {
        if (!color) return '';
        color = color.trim();
        if (color.startsWith('rgb')) return color;
        if (color.startsWith('#')) return color;
        const rgbMatch = color.match(/^(\d+)\s*,\s*(\d+)\s*,\s*(\d+)$/);
        if (rgbMatch) {
            return `rgb(${rgbMatch[1]},${rgbMatch[2]},${rgbMatch[3]})`;
        }
        return color;
    }

    private async enrichChannelWithGroup(channelSelected: Channel): Promise<ChannelAndGroup> {
        const channel = await this.channelService.getByRowId(this.vessel.id, channelSelected.rowId);
        if (!channel) return undefined;

        const groupList = await this.channelGroupService.getList(this.vessel.id);
        const categoryList = await this.channelCategoryService.getList(this.vessel.id);

        const group = groupList.find((g: ChannelGroup) => g.id === channel.groupId);
        if (group) {
            group.channelCategory = categoryList?.find((c: ChannelCategory) => c.id === group.categoryId);
        }

        const enriched: ChannelAndGroup = { ...channel, group } as ChannelAndGroup;
        return enriched;
    }

    private async getValveChannelByTag(tag: string): Promise<ChannelAndGroup> {
        if (!tag) return undefined;
        return await this.enrichChannelWithGroup({ rowId: tag } as Channel);
    }

    private buildValveSetting(): ValveSetting {
        return {
            way: this.selectedOpeningCount,
            orientation: this.selectedOrientation,
            percentage: this.selectedOpenPercentage,
            percentagePositionKey: this.selectedOpenPercentagePositionKey,
            open: {
                channelTag: this.selectedOpenChannel?.rowId?.toString() ?? null,
                channelIsDigital: this.selectedOpenChannel ? this.isChannelDigital(ValveActionType.OPEN) : null,
                state: this.selectedOpenChannelState,
                flashEnabled: this.isOpenToggleEnabled,
                flashChannelTag: this.selectedOpenFlashChannel?.rowId?.toString() ?? null,
            },
            close: {
                channelTag: this.selectedCloseChannel?.rowId?.toString() ?? null,
                channelIsDigital: this.selectedCloseChannel ? this.isChannelDigital(ValveActionType.CLOSE) : null,
                state: this.selectedCloseChannelState,
                flashEnabled: this.isCloseToggleEnabled,
                flashChannelTag: this.selectedCloseFlashChannel?.rowId?.toString() ?? null,
            },
        };
    }

    private resetValveState() {
        this.selectedOpeningCount = this.openingCountItemList[0];
        this.selectedOpenChannel = undefined;
        this.selectedCloseChannel = undefined;
        this.selectedOpenFlashChannel = undefined;
        this.selectedCloseFlashChannel = undefined;
        this.isOpenToggleEnabled = false;
        this.isCloseToggleEnabled = false;
        this.openChannelStateItemList = [];
        this.closeChannelStateItemList = [];
        this.selectedOpenChannelState = '';
        this.selectedCloseChannelState = '';
        this.openChannelStateColor = '';
        this.closeChannelStateColor = '';
        this.selectedOpenPercentage = 100;
        this.selectedClosePercentage = 100;
        this.selectedOpenPercentagePositionKey = 'mimic.settings.valve-object.interaction.position.bottom';
        this.selectedOpenPercentagePosition = 'bottom';
        this.selectedClosePercentagePositionKey = this.selectedOpenPercentagePositionKey;
        this.selectedClosePercentagePosition = this.selectedOpenPercentagePosition;
    }

    private async restoreValveSetting(valveSetting: ValveSetting) {
        if (!valveSetting) return;

        this.selectedOpeningCount = valveSetting.way ?? this.openingCountItemList[0];
        if (valveSetting.orientation) {
            this.selectedOrientation = valveSetting.orientation;
        }

        if (valveSetting.percentagePositionKey) {
            this.selectedOpenPercentagePositionKey = valveSetting.percentagePositionKey;
            this.selectedOpenPercentagePosition =
                this.percentagePositionMap[valveSetting.percentagePositionKey] || 'bottom';
            this.selectedClosePercentagePositionKey = this.selectedOpenPercentagePositionKey;
            this.selectedClosePercentagePosition = this.selectedOpenPercentagePosition;
        }

        await this.restoreActionSetting(ValveActionType.OPEN, valveSetting);
        await this.restoreActionSetting(ValveActionType.CLOSE, valveSetting);
    }

    private async restoreActionSetting(type: ValveActionType, valveSetting: ValveSetting) {
        const action = valveSetting[type];

        if (action?.channelTag) {
            const channel = await this.getValveChannelByTag(action.channelTag);
            if (channel) {
                this.setSelectedChannel(type, channel);
                this.setupChannelStateView(type);

                if (this.isChannelDigital(type) && action.state) {
                    this.setSelectedChannelState(type, action.state);
                    this.updateChannelStateColor(type);
                } else if (this.isChannelAnalog(type)) {
                    this.applyPercentage(valveSetting.percentage ?? 100);
                }
            }
        }
        this.setToggleEnabled(type, action?.flashEnabled ?? false);
        if (this.getToggleEnabled(type) && action?.flashChannelTag) {
            this.setSelectedFlashChannel(type, await this.getValveChannelByTag(action.flashChannelTag));
        }
    }

    hasChanges(): boolean {
        if (!this.isValveElement()) return false;

        const initial = this.initialValveSetting;
        if (!initial) {
            return !!(this.selectedOpenChannel || this.selectedCloseChannel);
        }
        return !_.isEqual(this.buildValveSetting(), initial);
    }

    resetValveIconToDefault(): void {
        if (!this.selectedElement) return;

        const group = this.selectedElement as SVGGElement;

        this.selectedOpeningCount = this.openingCountItemList[0];
        this.selectedOrientation = this.orientationItemList[0];

        this.removePercentageIndicator();
        this.elementOrientationService.applyValveOrientation(group, 'horizontal', 'static');

        group.querySelectorAll('path').forEach(path => {
            path.setAttribute('fill', ValveObjectSettingComponent.DEFAULT_VALVE_BODY_FILL);
            path.setAttribute('stroke', 'none');
            path.removeAttribute('stroke-width');
            path.style.fill = ValveObjectSettingComponent.DEFAULT_VALVE_BODY_FILL;
        });

        this.refreshSelectionFrame(group);
    }

    async save(): Promise<boolean> {
        this.editorService.showLoadingSpinner(true);
        try {
            const valveSetting = this.buildValveSetting();
            const elementSettings: ElementSettings = {
                id: this.elementSettingsId,
                channelTag: valveSetting.open.channelTag ?? '',
                channelIsDigital: valveSetting.open.channelIsDigital ?? false,
                elementIntegrationId: this.elementIntegration?.id ?? null,
                elementId: this.getSelectedElementId(),
                mimicId: this.mimicId,
                valveSetting,
            };

            if (!this.elementSettingsId) {
                const { newId } = await this.elementSettingsService.create(elementSettings);
                elementSettings.id = newId;
                this.elementSettingsId = newId;
            } else {
                await this.elementSettingsService.update(elementSettings);
            }

            this.initialValveSetting = _.cloneDeep(valveSetting);
            return true;
        } catch (err) {
            console.error(err);
        } finally {
            this.editorService.showLoadingSpinner(false);
        }
    }

    private getSelectedChannel(type: ValveActionType): ChannelAndGroup {
        return type === ValveActionType.OPEN ? this.selectedOpenChannel : this.selectedCloseChannel;
    }

    private updateValveIcon(): void {
        if (!this.selectedElement) return;

        const is3Way = this.selectedOpeningCount.includes('3way');
        if (is3Way) {
            this.applyValveIcon3Way();
        } else {
            this.applyValveIcon2Way();
        }
    }

    private applyValveIcon3Way(color?: string): void {
        if (!this.selectedElement) return;

        const orientation = this.getResolvedOrientation();
        const state = this.getValveDisplayState();
        const iconName = ValveObjectSettingComponent.VALVE_3WAY_ICON_BY_ORIENTATION[orientation];

        this.elementOrientationService
            .applyValveIcon3Way(this.selectedElement as SVGGElement, iconName, orientation, state)
            .subscribe(() => {
                const group = this.selectedElement as SVGGElement;
                const displayType = this.getValveDisplayType();
                const hasChannel = !!this.getSelectedChannel(displayType);
                const formattedColor = this.formatColorForCSS(color);

                group.querySelectorAll('path').forEach(path => {
                    if (formattedColor) {
                        path.setAttribute('fill', formattedColor);
                        path.style.fill = formattedColor;
                    }
                    if (hasChannel) {
                        path.setAttribute('stroke', '#2c3a48');
                        path.setAttribute('stroke-width', '1.5');
                    }
                });

                if (hasChannel && this.isChannelAnalog(displayType)) {
                    this.addPercentageIndicator();
                } else {
                    this.refreshSelectionFrame(group);
                }
            });
    }

    private applyValveIcon2Way(): void {
        if (!this.selectedElement) return;

        const orientation = this.getResolvedOrientation();
        const state = this.getValveDisplayState();

        this.elementOrientationService.applyValveOrientation(this.selectedElement as SVGGElement, orientation, state);
    }

    private getValveDisplayType(): ValveActionType {
        if (this.selectedCloseChannel && !this.selectedOpenChannel) {
            return ValveActionType.CLOSE;
        }
        return ValveActionType.OPEN;
    }

    private getValveDisplayState(): ValveDisplayState {
        return this.getValveDisplayType() === ValveActionType.OPEN && !this.isAnalogClosed()
            ? ValveDisplayState.STATIC
            : ValveDisplayState.CLOSED;
    }

    private isAnalogClosed(): boolean {
        return (
            this.isChannelAnalog(this.getValveDisplayType()) &&
            this.selectedOpenPercentage <= ValveObjectSettingComponent.ANALOG_CLOSED_MAX_PERCENTAGE
        );
    }

    private setSelectedChannel(type: ValveActionType, channel: ChannelAndGroup) {
        if (type === ValveActionType.OPEN) {
            this.selectedOpenChannel = channel;
        } else {
            this.selectedCloseChannel = channel;
        }
    }

    private setSelectedFlashChannel(type: ValveActionType, channel: ChannelAndGroup) {
        if (type === ValveActionType.OPEN) {
            this.selectedOpenFlashChannel = channel;
        } else {
            this.selectedCloseFlashChannel = channel;
        }
    }

    private getToggleEnabled(type: ValveActionType): boolean {
        return type === ValveActionType.OPEN ? this.isOpenToggleEnabled : this.isCloseToggleEnabled;
    }

    private setToggleEnabled(type: ValveActionType, enabled: boolean) {
        if (type === ValveActionType.OPEN) {
            this.isOpenToggleEnabled = enabled;
        } else {
            this.isCloseToggleEnabled = enabled;
        }
    }

    private setChannelStateItems(type: ValveActionType, items: string[]) {
        if (type === ValveActionType.OPEN) {
            this.openChannelStateItemList = items;
        } else {
            this.closeChannelStateItemList = items;
        }
    }

    private getSelectedChannelState(type: ValveActionType): string {
        return type === ValveActionType.OPEN ? this.selectedOpenChannelState : this.selectedCloseChannelState;
    }

    private setSelectedChannelState(type: ValveActionType, state: string) {
        if (type === ValveActionType.OPEN) {
            this.selectedOpenChannelState = state;
        } else {
            this.selectedCloseChannelState = state;
        }
    }

    private setChannelStateColor(type: ValveActionType, color: string) {
        if (type === ValveActionType.OPEN) {
            this.openChannelStateColor = color;
        } else {
            this.closeChannelStateColor = color;
        }
    }

    private getPercentage(type: ValveActionType): number {
        return type === ValveActionType.OPEN ? this.selectedOpenPercentage : this.selectedClosePercentage;
    }

    private setPercentage(type: ValveActionType, percentage: number) {
        if (type === ValveActionType.OPEN) {
            this.selectedOpenPercentage = percentage;
        } else {
            this.selectedClosePercentage = percentage;
        }
    }
}
